/**
 * Hazel's teddy collection.
 *
 * Used in three places: the game titles, the login avatars, and the card faces
 * in Sky's Memory Match. Adding a teddy is one entry here plus one photograph —
 * deliberately, because the collection will grow, and it has: ten became
 * twenty-one in one afternoon.
 *
 * Two turtles and two rabbits, which is what a real collection looks like. The
 * names are what must stay distinct, and a test holds that.
 */

export const TEDDY_KEYS = [
  'hoppity',
  'small-rabbit',
  'rudolf',
  'monkey',
  'sky',
  'stripey',
  'bluey',
  'turtley',
  'mickey',
  'rocky',
  'snowy',
  'big-rabbit',
  'nemo',
  'maritza',
  'rainbow',
  'owlie',
  'sammy',
  'tigger',
  'tommy',
  'spotty',
  'chaka',
] as const

export type TeddyKey = (typeof TEDDY_KEYS)[number]

export interface Teddy {
  readonly key: TeddyKey
  readonly name: string
  readonly species: string
}

export const TEDDIES: Readonly<Record<TeddyKey, Teddy>> = {
  hoppity: { key: 'hoppity', name: 'Hoppity', species: 'rabbit' },
  'small-rabbit': { key: 'small-rabbit', name: 'Small Rabbit', species: 'rabbit' },
  rudolf: { key: 'rudolf', name: 'Rudolf', species: 'reindeer' },
  monkey: { key: 'monkey', name: 'Monkey', species: 'monkey' },
  sky: { key: 'sky', name: 'Sky', species: 'octopus' },
  stripey: { key: 'stripey', name: 'Stripey', species: 'squishmallow' },
  bluey: { key: 'bluey', name: 'Bluey', species: 'squishmallow' },
  turtley: { key: 'turtley', name: 'Turtley', species: 'turtle' },
  mickey: { key: 'mickey', name: 'Mickey', species: 'mouse' },
  rocky: { key: 'rocky', name: 'Rocky', species: 'dog' },
  snowy: { key: 'snowy', name: 'Snowy', species: 'husky' },
  'big-rabbit': { key: 'big-rabbit', name: 'Big Rabbit', species: 'rabbit' },
  nemo: { key: 'nemo', name: 'Nemo', species: 'fish' },
  maritza: { key: 'maritza', name: 'Maritza', species: 'cactus' },
  // Recorded as what it certainly is rather than what it might be. The species
  // is read aloud — "Rainbow the ___" in the avatar picker, and on every one of
  // Sky's cards — so a guess at the animal under the antlers would be said out
  // loud to her a great many times. Stripey and Bluey are the same shape and
  // recorded the same way.
  rainbow: { key: 'rainbow', name: 'Rainbow', species: 'squishmallow' },
  owlie: { key: 'owlie', name: 'Owlie', species: 'owl' },
  sammy: { key: 'sammy', name: 'Sammy', species: 'shark' },
  tigger: { key: 'tigger', name: 'Tigger', species: 'tiger' },
  tommy: { key: 'tommy', name: 'Tommy', species: 'tomato' },
  spotty: { key: 'spotty', name: 'Spotty', species: 'turtle' },
  chaka: { key: 'chaka', name: 'Chaka', species: 'parrot' },
}

export const ALL_TEDDIES: readonly Teddy[] = TEDDY_KEYS.map((key) => TEDDIES[key])

export function isTeddyKey(value: string): value is TeddyKey {
  return (TEDDY_KEYS as readonly string[]).includes(value)
}

/** Resolve a teddy from untrusted input, such as a stored avatar_key. */
export function getTeddy(key: string | null | undefined): Teddy | undefined {
  return typeof key === 'string' && isTeddyKey(key) ? TEDDIES[key] : undefined
}

/**
 * Where a teddy's picture lives, relative to the web app's public root.
 *
 * Drawn placeholders for now, in the app's own palette, so nothing anywhere is
 * blocked on a camera. They are deliberately a *set* — one construction, one
 * backdrop, species carried by silhouette and identity by colour — because ten
 * pictures taken ten different ways look like a jumble when Sky's Memory Match
 * puts them side by side. That is the same thing DOL-34 asks for from the real
 * photographs.
 *
 * Square, and one shape only. There used to be a 3:2 crop as well, for the
 * backdrop behind a game's title — every game has finished artwork now, so
 * nothing read them and ten files sat in the repo doing nothing.
 */
export function teddyPhotoPath(key: TeddyKey): string {
  // Served as WebP: a tenth the size of the PNG a photograph arrives as, and a
  // memory board asks for ten of them at once.
  return `/teddies/${key}.webp`
}

/**
 * The drawn stand-in for a teddy with no photograph yet.
 *
 * Kept as its own path rather than folded into the one above, so a missing
 * photograph falls back to a picture of the right teddy rather than to nothing
 * at all — a card that shows a name and a blank is worse than one that shows
 * the drawing it always had.
 *
 * Only the first ten have one. They were drawn before there were photographs,
 * and the eleven that came later arrived photographed, so there was never a
 * gap for a stand-in to fill. For those this path names a file that is not
 * there — which `fallBackToDrawing` already copes with by hiding the image,
 * the same as it would for a drawing that failed to load.
 */
export function teddyPlaceholderPath(key: TeddyKey): string {
  return `/teddies/${key}.svg`
}
